import { useCallback, useEffect, useState } from 'react';
import { salesApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ WHO RUNS WHAT, WHO CAN GET YOU IN, WHAT JUST CHANGED ═══
 *
 * ⚠ THE COMPLAINTS ARE THE POINT. Nothing you invent about a rival lands like a sentence another
 * principal said about them out loud — and once four schools have said the same thing, that sentence
 * belongs in every demo you give against them.
 *
 * ⚠ AND A SCHOOL THAT HAS NOT AGREED TO BE NAMED NEVER APPEARS IN A SUGGESTION. That gate is in the
 * domain, not in this file, so nothing here can soften it into "a school in Mangaluru".
 */

interface Renewing { school: string; renewsOn: string; inDays: number }
interface Competitor {
  name: string; schoolsRunningIt: number; wonAgainst: number; lostTo: number;
  typicalAnnualPaise: number | null; gripes: string[]; strengths: string[]; renewingSoon: Renewing[];
}
interface SignalT {
  id: string; prospectId: string | null; directoryEntryId: string | null; schoolName: string;
  kind: string; kindLabel: string; body: string; source: string | null;
  heardOn: string; actOn: string; isDue: boolean; isStale: boolean;
}
interface ReferralT {
  id: string; toProspectId: string; toName: string; from: string;
  on: string; note: string | null; thankedFor: boolean;
}
interface Intel {
  competitors: { competitors: Competitor[]; notes: string[] };
  dueSignals: SignalT[]; staleSignals: SignalT[];
  unthanked: ReferralT[]; referralChannel: string;
}

function rupees(paise: number): string {
  const r = Math.round(paise / 100);
  const s = String(r);
  if (s.length <= 3) return `₹${s}`;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  const g: string[] = [];
  while (rest.length > 2) { g.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
  if (rest) g.unshift(rest);
  return `₹${g.join(',')},${last3}`;
}

export function MarketIntel() {
  const [intel, setIntel] = useState<Intel | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const v = await salesApi.get<Intel>('/api/platform/sales/intel');
      setIntel(v && Array.isArray(v.dueSignals) ? v : null);
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not load the market.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const act = async (path: string, body: unknown, id: string) => {
    setBusy(id);
    try { await salesApi.post(path, body); await load(); }
    catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(null); }
  };

  if (!intel) {
    return <div className="card" style={{ padding: 24, color: 'var(--ink-3)' }}>
      {error ?? 'Loading…'}
    </div>;
  }

  return (
    <div>
      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 10px' }}>{error}</p>}

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontWeight: 650, marginBottom: 6 }}>The market</div>
        <ul style={{ margin: '0 0 8px', paddingLeft: 16 }}>
          {intel.competitors.notes.map((n, i) => (
            <li key={i} style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 3 }}>{n}</li>
          ))}
          {/* ⚠ The number that decides where to spend your time, and most founders never measure it. */}
          <li style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{intel.referralChannel}</li>
        </ul>
      </div>

      {intel.dueSignals.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 650, marginBottom: 2 }}>Worth a call now</div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 10px' }}>
            A new principal is worth a call in their first month and nothing in their eighth.
          </p>
          {intel.dueSignals.map((s) => (
            <div key={s.id} style={{
              display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
              borderTop: '1px solid var(--line-2)', padding: '8px 0',
            }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{s.schoolName}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{s.kindLabel}</span>
              <span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                {s.body}{s.source ? ` — ${s.source}` : ''}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button className="btn" disabled={busy === s.id} style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={() => void act(`/api/platform/sales/intel/signals/${s.id}/acted`, { outcome: null }, s.id)}>
                  Done
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {intel.unthanked.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 650, marginBottom: 2 }}>Say thank you</div>
          {/* ⚠ Not sentiment. An unthanked referrer does not make a second introduction, and in a
              channel that is mostly word of mouth that is the difference between one and five. */}
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 10px' }}>
            A referral that goes unthanked is a referrer who does not make a second one.
          </p>
          {intel.unthanked.map((r) => (
            <div key={r.id} style={{
              display: 'flex', gap: 10, alignItems: 'baseline',
              borderTop: '1px solid var(--line-2)', padding: '8px 0',
            }}>
              <span style={{ fontSize: 12.5 }}>
                <strong>{r.from}</strong> introduced you to {r.toName} on {r.on}
              </span>
              <button className="btn" disabled={busy === r.id}
                      style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11 }}
                      onClick={() => void act(`/api/platform/sales/intel/referrals/${r.id}/thanked`, {}, r.id)}>
                Thanked
              </button>
            </div>
          ))}
        </div>
      )}

      {intel.competitors.competitors.map((c) => (
        <div key={c.name} className="card" style={{ padding: 16, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 650, fontSize: 14 }}>{c.name}</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              {c.schoolsRunningIt} school{c.schoolsRunningIt === 1 ? '' : 's'}
              {c.wonAgainst > 0 ? ` · ${c.wonAgainst} won from them` : ''}
              {c.lostTo > 0 ? ` · ${c.lostTo} lost to them` : ''}
              {/* ⚠ The median, not the mean — one school paying five times the going rate would
                  drag an average somewhere nobody actually pays. */}
              {c.typicalAnnualPaise ? ` · typically ${rupees(c.typicalAnnualPaise)} a year` : ''}
            </span>
          </div>

          {c.renewingSoon.length > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--ink)', marginTop: 6 }}>
              {c.renewingSoon.map((r) => (
                <div key={r.school}>Out of contract: <strong>{r.school}</strong> in {r.inDays} days ({r.renewsOn})</div>
              ))}
            </div>
          )}

          {c.gripes.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 3 }}>What their customers say</div>
              {c.gripes.map((g, i) => (
                <div key={i} style={{ fontSize: 12.5, color: 'var(--ink-2)', fontStyle: 'italic' }}>“{g}”</div>
              ))}
            </div>
          )}

          {c.strengths.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {/* ⚠ Kept on purpose. A rival's genuine strength is the thing you must not walk into,
                  and a file of only complaints prepares you for a conversation that does not happen. */}
              <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 3 }}>What they are good at</div>
              {c.strengths.map((g, i) => (
                <div key={i} style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>“{g}”</div>
              ))}
            </div>
          )}
        </div>
      ))}

      {intel.staleSignals.length > 0 && (
        <div className="card" style={{ padding: 16, marginTop: 8 }}>
          {/* ⚠ A separate list. A signal heard eight months ago and never acted on is worth seeing
              once; leaving it in the due list is how the due list stops being read. */}
          <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 6 }}>Heard, and never acted on</div>
          {intel.staleSignals.map((s) => (
            <div key={s.id} style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 3 }}>
              {s.schoolName} · {s.kindLabel} · {s.body} (heard {s.heardOn})
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
